import { Destroyable } from './Destroyable';

export const Listener = (() => {
	const _name = Symbol('name');
	const _callbacks = Symbol('callbacks');

	class Listener extends Destroyable {
		constructor(name) {
			super();
			
			this[_name] = null;
			this[_callbacks] = [];
			
			if(name)
				this.name = name;
		}
		destroy() {
			this[_name] = null;
			this.callbacks.length = 0;
			
			super.destroy();
		}
		get name() {
			return this[_name];
		}
		set name(v) {
			if(typeof v !== 'string')
				throw new TypeError('name must be a string.');
			this[_name] = v;
		}
		get callbacks() {
			return this[_callbacks];
		}
	}

	return Listener;
})();

export const ListenerCallback = (() => {
	const _callback = Symbol('callback');
	const _selector = Symbol('selector');
	const _triggersAllowed = Symbol('triggersAllowed');
	const _triggerCount = Symbol('triggerCount');
	
	class ListenerCallback extends Destroyable {
		constructor(callback, selector, triggersAllowed) {
			super();
			
			this[_callback] = null;
			this[_selector] = null;
			this[_triggersAllowed] = null;
			this[_triggerCount] = 0;
			
			if(callback)
				this.callback = callback;
			if(selector)
				this.selector = selector;
			if([null, undefined].indexOf(triggersAllowed) === -1)
				this.triggersAllowed = triggersAllowed;
		}
		destroy() {
			this[_callback] = null;
			this[_selector] = null;
			this[_triggersAllowed] = null;
			this[_triggerCount] = 0;
			
			super.destroy();
		}
		get callback() {
			return this[_callback];
		}
		set callback(v) {
			if(this[_callback])
				throw new Error('callback has already been set.');
			if(typeof v !== 'function')
				throw new TypeError('callback must be a function.');
			this[_callback] = v;
		}
		get selector() {
			return this[_selector];
		}
		set selector(v) {
			if(this.selector)
				throw new Error('selector has already been set.');
			if(typeof v !== 'string')
				throw new TypeError('selector must be a string.');
			this[_selector] = v;
		}
		get triggersAllowed() {
			return this[_triggersAllowed];
		}
		set triggersAllowed(v) {
			if([null, undefined].indexOf(this.triggersAllowed) === -1)
				throw new Error('triggersAllowed has already been set.');
			if(!Number.isInteger(v))
				throw new TypeError('triggersAllowed must be an integer.');
			this[_triggersAllowed] = v;
		}
		get triggerCount() {
			return this[_triggerCount];
		}
		set triggerCount(v) {
			if(!Number.isInteger(v))
				throw new TypeError('triggerCount must be an integer.');
			this[_triggerCount] = v;
		}
	}

	return ListenerCallback;
})();

export const EventEmitterMixin = (() => {
	const _listeners = Symbol('listeners');
	
	const EventEmitterMixin = superclass => class extends superclass {
		constructor(...args) {
			super(...args);
			
			this[_listeners] = [];
		}
		get listeners() {
			return this[_listeners].slice(0);
		}
		destroy() {
			this.off();
			this[_listeners].length = 0;
			
			super.destroy();
		}
		on(name, callback, triggersAllowed) {
			if(typeof name !== 'string')
				throw new TypeError('name must be a string.');
			if(typeof callback !== 'function')
				throw new TypeError('callback must be a function.');
			if(triggersAllowed && !Number.isInteger(triggersAllowed))
				throw new TypeError('triggersAllowed must be an Integer.');
			
			var selector = null;
			name.match(/^([^.]*)\.?(.*)$/).forEach(function(v, index) {
				switch(index) {
					case 1: name = v; break;
					case 2: selector = v; break;
				}
			});
			
			var listener = this[_listeners].filter(function(listener) {
				return listener.name === name;
			})[0];
			
			if(!listener) {
				listener = new Listener(name);
				this[_listeners].push(listener);
			}
			listener.callbacks.push(
				new ListenerCallback(callback, selector, triggersAllowed)
			);
		}
		one(name, callback) {
			this.on(name, callback, 1);
		}
		off(name, callback) {
			if(name && typeof name !== 'string')
				throw new TypeError('name must be null or a string.');
			if(callback && typeof callback !== 'function')
				throw new TypeError('callback must be null or a function.');
			
			var selector = null;
			if(name)
				name.match(/^([^.]*)\.?(.*)$/).forEach(function(v, index) {
					switch(index) {
						case 1: name = v; break;
						case 2: selector = v; break;
					}
				});
			
			//filter for all or only matching listeners
			this[_listeners]
				.filter(function(listener) {
					return !name || listener.name === name;
				})
				.forEach(function(listener) {
					listener.callbacks
						//filter for all or only matching callbacks
						.filter(function(listenerCallback) {
							return !callback || listenerCallback.callback === callback;
						})
						//filter for all or only matching selectors
						.filter(function(listenerCallback) {
							return !selector || listenerCallback.selector === selector;
						})
						//remove the matching callbacks
						.forEach(function(listenerCallback) {
							var callbackIndex = listener.callbacks.indexOf(listenerCallback);
							if(callbackIndex !== -1)
								listener.callbacks.splice(callbackIndex, 1);
						});
				});
		}
		trigger(name, ...args) {
			var listener = this[_listeners].filter(function(listener) {
				return listener.name === name;
			})[0];
			
			if(!listener)
				return;
			
			listener.callbacks
				.slice()
				.forEach(function(listenerCallback, index) {
					listenerCallback.callback.call(this, ...args);
					listenerCallback.triggerCount++;
					if(listenerCallback.triggersAllowed === null)
						return;
					if(listenerCallback.triggersAllowed > listenerCallback.triggerCount) 
						return;
					
					listener.callbacks.splice(index, 1);
				});
		}
	};
	return EventEmitterMixin;
})();